// ============================================================================
// Mini activity log for one record (Task 1). Shows who routed it, to whom,
// status changes, and notes — a compact timeline. Reused on any record detail.
// ============================================================================
import { useRecordRouting, type RoutingEntityType, type RoutingStatus } from './hooks/use-routing-data';

const STATUS_CLASSES: Record<RoutingStatus, string> = {
  Open: 'bg-st-warn-bg text-st-warn-fg',
  Acknowledged: 'bg-st-info-bg-strong text-st-info-fg',
  Resolved: 'bg-rag-green/15 text-rag-green',
  Cancelled: 'bg-tcs-surface text-tcs-text-muted',
};

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface Props {
  entityType: RoutingEntityType;
  entityId: string;
}

export function RecordActivityLog({ entityType, entityId }: Props) {
  const { routings, eventsByRouting, isLoading, error } = useRecordRouting(entityType, entityId);

  if (isLoading) {
    return <p className="text-xs text-tcs-text-muted">Loading routing activity…</p>;
  }
  if (error) {
    return <p className="text-xs text-rag-red">{error}</p>;
  }
  if (routings.length === 0) {
    return <p className="text-xs text-tcs-text-muted">No routing activity yet.</p>;
  }

  return (
    <ul className="space-y-3">
      {routings.map((r) => (
        <li key={r.routing_id} className="rounded-md border border-tcs-border bg-tcs-bg p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 text-sm text-tcs-text">
              <span className="font-medium">{r.routed_from_name ?? r.routed_from_eid}</span>
              <span className="text-tcs-text-muted"> routed to </span>
              <span className="font-medium">
                {r.routed_to_name ?? (r.routed_to_role ? `${r.routed_to_role.toUpperCase()} team` : '—')}
              </span>
            </div>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_CLASSES[r.status]}`}
            >
              {r.status}
            </span>
          </div>
          {r.reason && <p className="mt-1 whitespace-pre-wrap text-xs text-tcs-text-secondary">{r.reason}</p>}
          <ol className="mt-2 space-y-1 border-l border-tcs-border pl-3">
            {(eventsByRouting[r.routing_id] ?? []).map((ev) => (
              <li key={ev.event_id} className="text-[11px] text-tcs-text-muted">
                <span className="font-mono">{fmtTime(ev.created_at)}</span>
                {' · '}
                <span className="font-medium text-tcs-text-secondary">{ev.actor_name ?? ev.actor_eid ?? 'system'}</span>
                {' '}
                {ev.event_type === 'routed'
                  ? 'routed'
                  : ev.event_type === 'resolved'
                  ? 'resolved'
                  : ev.event_type === 'status_change'
                  ? 'updated status'
                  : 'note'}
                {ev.note ? ` — ${ev.note}` : ''}
              </li>
            ))}
          </ol>
        </li>
      ))}
    </ul>
  );
}
